import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { ViewTabs } from '@/components/list/ViewTabs';
import { ChevronLeft } from 'lucide-react';
import { PageHead } from '@/admin-area/ui/PageHead';
import { ConfigProblem, ConfigurationGate } from './ConfigurationParts';
import { ConfigurationHistory } from './ConfigurationHistory';
import { listNoteTypes, type NoteType, type Category } from './api';
import { TypeCategories } from './TypeCategories';
import { LinkedPurposes } from './LinkedPurposes';

export function NoteTypesPage() {
  return (
    <ConfigurationGate>
      <NoteTypes />
    </ConfigurationGate>
  );
}
function NoteTypes() {
  const { t } = useTranslation('expenses');
  const [params, setParams] = useSearchParams();
  const type = params.get('type') === 'reimbursement' ? 'reimbursement' : 'travel';
  const [data, setData] = useState<{ items: NoteType[]; categories: Category[] } | null>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let live = true;
    setData(null);
    setError('');
    void listNoteTypes().then((outcome) => {
      if (!live) return;
      if (outcome.ok) setData(outcome.value);
      else setError(outcome.code);
    });
    return () => {
      live = false;
    };
  }, [retry]);
  return (
    <section className="flex min-w-0 max-w-4xl flex-col gap-5">
      <Link
        className="text-foreground font-semibold hover:underline inline-flex min-h-11 items-center"
        to="/expenses/settings/purposes"
      >
        <ChevronLeft aria-hidden="true" className="size-4" />
        {t('configuration.title')}
      </Link>
      <PageHead title={t('configuration.noteTypes')} summary={t('configuration.typesSummary')} />
      <ViewTabs
        label={t('configuration.noteType')}
        views={(['travel', 'reimbursement'] as const).map((id) => ({
          id,
          label: t(`reports.noteType.${id}`),
          params: { type: id },
        }))}
        current={{ get: (key) => (key === 'type' ? type : null) }}
        onSelect={(view) => setParams({ type: view.id })}
      />
      {error ? <ConfigProblem code={error} retry={() => setRetry((n) => n + 1)} /> : null}
      {!error && data === null ? <p role="status">{t('configuration.loading')}</p> : null}
      {data ? (
        <NoteTypeEditor
          key={type}
          setting={data.items.find((s) => s.noteType === type)!}
          categories={data.categories}
          refresh={() => setRetry((n) => n + 1)}
        />
      ) : null}
    </section>
  );
}

function NoteTypeEditor({
  setting,
  categories,
  refresh,
}: {
  readonly setting: NoteType;
  readonly categories: Category[];
  readonly refresh: () => void;
}) {
  return (
    <div className="space-y-6">
      <TypeCategories setting={setting} categories={categories} refresh={refresh} />
      <LinkedPurposes type={setting.noteType} />
      <ConfigurationHistory
        id={setting.noteType}
        kind="note_type"
        categories={categories}
        revision={setting.revision}
      />
    </div>
  );
}
